import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import type { CitaCalendario, AgendaConfig } from '../store/secretaryStore';

function mapCitasDigitales(citasData: any[]): CitaCalendario[] {
  return citasData.map((row) => {
    const paciente = Array.isArray(row.paciente) ? row.paciente[0] : row.paciente;
    const perfilProf = Array.isArray(row.perfil_profesional) ? row.perfil_profesional[0] : row.perfil_profesional;
    const profesionalData: any = perfilProf?.perfiles_usuario;
    const profesional = Array.isArray(profesionalData) ? profesionalData[0] : profesionalData;
    const faq = Array.isArray(row.profesional_servicios_faq) ? row.profesional_servicios_faq[0] : row.profesional_servicios_faq;

    const nombrePaciente = paciente
      ? `${paciente.nombres_completos ?? ''} ${paciente.apellidos_completos ?? ''}`.trim()
      : 'Paciente desconocido';

    const nombreProfesional = profesional
      ? `${profesional.nombres_completos ?? ''} ${profesional.apellidos_completos ?? ''}`.trim()
      : row.profesional_uuid.slice(0, 8);

    return {
      cita_id: row.cita_id,
      paciente_nombre: nombrePaciente,
      profesional_uuid: row.profesional_uuid,
      profesional_nombre: nombreProfesional,
      fecha_hora_inicio: row.fecha_hora_inicio,
      fecha_hora_fin: row.fecha_hora_fin,
      estado: row.estado,
      servicio_titulo: faq?.titulo_opcion ?? null,
      tipo: 'cita_digital' as const
    };
  });
}

function mapEventosManuales(eventosData: any[], perfilesMap: Record<string, any>, year: number, month: number, day: number): CitaCalendario[] {
  return eventosData.map((row) => {
    let pName = 'Bloqueo / Ocupado';
    if (row.tipo === 'cita_manual') {
      if (row.paciente_uuid && perfilesMap[row.paciente_uuid]) {
        const p = perfilesMap[row.paciente_uuid];
        pName = `${p.nombres_completos} ${p.apellidos_completos}`;
      } else if (row.paciente_data && typeof row.paciente_data === 'object' && ('nombre' in row.paciente_data || 'nombres_completos' in row.paciente_data)) {
        pName = String(row.paciente_data.nombre || row.paciente_data.nombres_completos);
      } else {
        pName = 'Agenda Presencial';
      }
    }

    const [startH, startM] = row.inicio.split(':');
    const startDateTime = new Date(year, month - 1, day, Number.parseInt(startH, 10), Number.parseInt(startM, 10));
    
    const [endH, endM] = row.fin.split(':');
    const endDateTime = new Date(year, month - 1, day, Number.parseInt(endH, 10), Number.parseInt(endM, 10));

    return {
      cita_id: row.evento_id,
      paciente_nombre: pName,
      profesional_uuid: row.profesional_uuid,
      profesional_nombre: 'Profesional (Cita Manual)',
      fecha_hora_inicio: startDateTime.toISOString(),
      fecha_hora_fin: endDateTime.toISOString(),
      estado: row.tipo,
      servicio_titulo: null,
      tipo: row.tipo as 'cita_manual' | 'ocupado',
      nota: row.nota || undefined
    };
  });
}

async function fetchPerfilesForManualEvents(eventosData: any[] | null): Promise<Record<string, any>> {
  const perfilesMap: Record<string, { nombres_completos: string; apellidos_completos: string }> = {};
  const pacienteUuids = eventosData?.map(e => e.paciente_uuid).filter(Boolean) || [];
  
  if (pacienteUuids.length > 0) {
    const { data: perfiles } = await supabase
      .from('perfiles_usuario')
      .select('perfil_usuario_id, nombres_completos, apellidos_completos')
      .in('perfil_usuario_id', pacienteUuids);
      
    if (perfiles) {
      perfiles.forEach((p: any) => {
        perfilesMap[p.perfil_usuario_id] = p;
      });
    }
  }
  return perfilesMap;
}

export async function fetchConfigsForProfessionals(uniqueProfUuids: string[]): Promise<Record<string, AgendaConfig>> {
  const configs: Record<string, AgendaConfig> = {};
  
  if (uniqueProfUuids.length > 0) {
    const { data: configData, error: configError } = await supabase
      .from('profesional_config_agenda')
      .select('profesional_uuid, disponibilidad_jsonb')
      .in('profesional_uuid', uniqueProfUuids);

    if (configError) {
      console.error('[CalendarService] Error fetching configs:', configError.message);
    } else if (configData) {
      for (const row of configData) {
        if (row.disponibilidad_jsonb) {
          configs[row.profesional_uuid] = row.disponibilidad_jsonb as any;
        }
      }
    }
  }
  return configs;
}

async function fetchPersonalWorkspaceProfesionales(activeWorkspaceId: string, allowedProfesionales: string[], workspaceProfesionales: { uuid: string, nombre: string }[]) {
  const { data: nData, error } = await supabase
    .from('unidades_negocio')
    .select(`
      vendedor_uuid,
      vendedor (
        perfiles_usuario (
          nombres_completos,
          apellidos_completos
        )
      )
    `)
    .eq('negocio_uuid', activeWorkspaceId)
    .single();

  if (error) {
    console.error('[CalendarService] Error fetching unidades_negocio:', error.message);
  } else if (nData?.vendedor_uuid) {
    allowedProfesionales.push(nData.vendedor_uuid);
    
    const v: any = Array.isArray(nData.vendedor) ? nData.vendedor[0] : nData.vendedor;
    const p: any = v?.perfiles_usuario;
    const pData: any = Array.isArray(p) ? p[0] : p;
    const nombre = pData && (pData.nombres_completos || pData.apellidos_completos)
      ? `${pData.nombres_completos || ''} ${pData.apellidos_completos || ''}`.trim() 
      : 'Profesional Principal';
      
    workspaceProfesionales.push({ uuid: nData.vendedor_uuid, nombre });
  }
}

async function fetchEquipoProfesionales(activeWorkspaceId: string, allowedProfesionales: string[], workspaceProfesionales: { uuid: string, nombre: string }[]) {
  const { data: eData, error } = await supabase
    .from('equipo_miembros')
    .select(`
      miembro_uuid,
      perfil:perfiles_usuario!equipo_miembros_miembro_uuid_fkey (
        nombres_completos,
        apellidos_completos
      )
    `)
    .eq('equipo_uuid', activeWorkspaceId)
    .in('rol_equipo', ['admin', 'medico']); // Solo traemos a los médicos al calendario, no a las secretarias

  if (error) {
    console.error('[CalendarService] Error fetching equipo_miembros:', error.message);
  } else if (eData) {
    for (const row of eData) {
      allowedProfesionales.push(row.miembro_uuid);
      
      const p: any = Array.isArray(row.perfil) ? row.perfil[0] : row.perfil;
      const nombre = p && (p.nombres_completos || p.apellidos_completos)
        ? `${p.nombres_completos || ''} ${p.apellidos_completos || ''}`.trim() 
        : 'Médico del Equipo';
        
      workspaceProfesionales.push({ uuid: row.miembro_uuid, nombre });
    }
  }
}

async function fetchWorkspaceProfesionales(activeWorkspaceId?: string, workspaceType: 'personal' | 'equipo' = 'personal'): Promise<{ allowedProfesionales: string[], workspaceProfesionales: { uuid: string, nombre: string }[] }> {
  const allowedProfesionales: string[] = [];
  const workspaceProfesionales: { uuid: string, nombre: string }[] = [];
  
  if (!activeWorkspaceId) return { allowedProfesionales, workspaceProfesionales };

  if (workspaceType === 'personal') {
    await fetchPersonalWorkspaceProfesionales(activeWorkspaceId, allowedProfesionales, workspaceProfesionales);
  } else if (workspaceType === 'equipo') {
    await fetchEquipoProfesionales(activeWorkspaceId, allowedProfesionales, workspaceProfesionales);
  }

  return { allowedProfesionales, workspaceProfesionales };
}

// ─── Servicio de Calendario ────────────────────────────────────────────────────
// Solo lee datos logísticos de agendamiento_citas. El RLS de Supabase garantiza
// que la secretaria solo ve citas de su workspace. Nunca toca la Bóveda Clínica.

export async function fetchCitasCalendario(
  dateIso: string,
  showCancelled: boolean = false,
  activeWorkspaceId?: string,
  workspaceType: 'personal' | 'equipo' = 'personal'
): Promise<{ citas: CitaCalendario[]; configs: Record<string, any>; workspaceProfesionales: { uuid: string, nombre: string }[] }> {
  // Calculamos el inicio y fin del día en la zona horaria local (Caracas)
  const [year, month, day] = dateIso.split('-').map(Number);
  const localStart = new Date(year, month - 1, day, 0, 0, 0);
  const localEnd = new Date(year, month - 1, day, 23, 59, 59, 999);

  // Convertimos a ISO para consultar a la base de datos (que guarda timestamptz en UTC)
  const startOfDay = localStart.toISOString();
  const endOfDay = localEnd.toISOString();

  // 0. Determinar los profesionales del workspace activo
  const { allowedProfesionales, workspaceProfesionales } = await fetchWorkspaceProfesionales(activeWorkspaceId, workspaceType);

  // 1. Fetch de Citas Digitales (App)
  let query = supabase
    .from(workspaceType === 'equipo' ? 'agendamiento_equipo' : 'agendamiento_citas')
    .select(`
      cita_id,
      paciente_uuid,
      profesional_uuid,
      fecha_hora_inicio,
      fecha_hora_fin,
      estado,
      profesional_servicios_faq ( titulo_opcion ),
      paciente:perfiles_usuario!${workspaceType === 'equipo' ? 'agendamiento_equipo_paciente_uuid_fkey' : 'agendamiento_citas_paciente_uuid_fkey'} (
        nombres_completos,
        apellidos_completos
      ),
      perfil_profesional:perfiles_profesionales!${workspaceType === 'equipo' ? 'agendamiento_equipo_profesional_uuid_fkey' : 'agendamiento_citas_profesional_uuid_fkey'} (
        perfiles_usuario (
          nombres_completos,
          apellidos_completos
        )
      )
    `)
    .gte('fecha_hora_inicio', startOfDay)
    .lte('fecha_hora_inicio', endOfDay);

  if (allowedProfesionales.length > 0) {
    query = query.in('profesional_uuid', allowedProfesionales);
  }

  if (showCancelled) {
    query = query.not('estado', 'in', '("expirada","rechazada")');
  } else {
    query = query.not('estado', 'in', '("expirada","rechazada","cancelada_paciente")');
  }

  const { data: citasData, error: citasError } = await query;

  if (citasError) {
    console.error('[CalendarService] Error fetching citas digitales:', citasError.message);
  }

  // 2. Fetch de Eventos de Agenda (Manuales y Bloqueos)
  let eventosQuery = supabase
    .from('agenda_eventos')
    .select(`
      evento_id,
      profesional_uuid,
      inicio,
      fin,
      tipo,
      nota,
      paciente_data,
      paciente_uuid
    `)
    .eq('fecha', dateIso);

  if (allowedProfesionales.length > 0) {
    eventosQuery = eventosQuery.in('profesional_uuid', allowedProfesionales);
  }

  const { data: eventosData, error: eventosError } = await eventosQuery;

  if (eventosError) {
    console.error('[CalendarService] Error fetching eventos manuales:', eventosError.message);
  }

  // Si hay pacientes registrados asociados a citas manuales, buscar sus nombres
  const perfilesMap = await fetchPerfilesForManualEvents(eventosData);

  let merged: CitaCalendario[] = [];

  if (citasData) {
    merged = [...merged, ...mapCitasDigitales(citasData)];
  }

  if (eventosData) {
    merged = [...merged, ...mapEventosManuales(eventosData, perfilesMap, year, month, day)];
  }

  // Ordenar por hora de inicio ascendente
  merged.sort((a, b) => new Date(a.fecha_hora_inicio).getTime() - new Date(b.fecha_hora_inicio).getTime());
  const citas = merged;

  // 3. Fetch Configs (disponibilidad_jsonb) de los profesionales encontrados
  const uniqueProfUuids = Array.from(new Set(citas.map(c => c.profesional_uuid)));
  const configs = await fetchConfigsForProfessionals(uniqueProfUuids);

  return { citas, configs, workspaceProfesionales };
}

/**
 * Actualiza el estado de una cita usando la RPC segura (Zero-Trust).
 * Esto evita el uso de UPDATE directo desde el cliente y garantiza Audit Trail.
 */
export async function updateEstadoCita(citaId: string, nuevoEstado: string, isManual: boolean = false): Promise<{ success: boolean; message?: string }> {
  try {
    if (isManual) {
      // Para citas manuales (Shadow Profiles / Bloqueos), cancelar significa eliminar el evento
      const { error } = await supabase
        .from('agenda_eventos')
        .delete()
        .eq('evento_id', citaId);

      if (error) {
        console.error('[CalendarService] Error al eliminar cita manual:', error.message);
        return { success: false, message: error.message };
      }
      return { success: true };
    }

    const { data, error } = await supabase.rpc('fn_actualizar_estado_cita', {
      p_cita_id: citaId,
      p_nuevo_estado: nuevoEstado
    });

    if (error) {
      console.error('[CalendarService] Error RPC al actualizar cita:', error.message);
      return { success: false, message: error.message };
    }

    if (data && !data.success) {
      console.warn('[CalendarService] Rechazo lógico en actualización:', data.message);
      return { success: false, message: data.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('[CalendarService] Excepción al actualizar cita:', err);
    return { success: false, message: err?.message || 'Error desconocido' };
  }
}

/**
 * Suscribe a los eventos en tiempo real de la tabla agendamiento_citas.
 * Supabase aplica el RLS automáticamente, de modo que solo recibimos cambios
 * para las citas de nuestro workspace activo.
 * 
 * Devuelve una función para de-suscribirse.
 */
export function subscribeToCitasRealtime(onUpdate: () => void): () => void {
  const channel = supabase
    .channel('agendamiento_citas_changes')
    .on(
      'postgres_changes',
      {
        event: '*', // Escucha INSERT, UPDATE y DELETE
        schema: 'public',
        table: 'agendamiento_citas'
      },
      (payload) => {
        console.log('[CalendarService] Realtime Update detectado:', payload);
        onUpdate();
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Reagenda una cita existente hacia un nuevo bloque de horario y/o profesional.
 * Llama a la RPC segura (Zero-Trust) para asegurar que no haya double-booking.
 */
export async function reagendarCitaLogistica(
  citaId: string,
  nuevaFechaInicioISO: string,
  nuevaFechaFinISO: string,
  nuevoProfesionalUuid: string,
  isManual: boolean = false
): Promise<{ success: boolean; message?: string }> {
  try {
    if (isManual) {
      // Extraemos la fecha y hora local desde el string ISO en UTC
      const startObj = new Date(nuevaFechaInicioISO);
      const yyyy = startObj.getFullYear();
      const mm = String(startObj.getMonth() + 1).padStart(2, '0');
      const dd = String(startObj.getDate()).padStart(2, '0');
      const fechaLocal = `${yyyy}-${mm}-${dd}`;
      
      const shh = String(startObj.getHours()).padStart(2, '0');
      const smin = String(startObj.getMinutes()).padStart(2, '0');
      const inicioTime = `${shh}:${smin}:00`;

      const endObj = new Date(nuevaFechaFinISO);
      const ehh = String(endObj.getHours()).padStart(2, '0');
      const emin = String(endObj.getMinutes()).padStart(2, '0');
      const finTime = `${ehh}:${emin}:00`;

      const { error } = await supabase
        .from('agenda_eventos')
        .update({
          inicio: inicioTime,
          fin: finTime,
          profesional_uuid: nuevoProfesionalUuid,
          fecha: fechaLocal
        })
        .eq('evento_id', citaId);

      if (error) {
        console.error('[CalendarService] Error reagendando cita manual:', error.message);
        return { success: false, message: error.message };
      }
      return { success: true };
    }

    const { data, error } = await supabase.rpc('fn_reagendar_cita', {
      p_cita_id: citaId,
      p_nueva_fecha_inicio: nuevaFechaInicioISO,
      p_nueva_fecha_fin: nuevaFechaFinISO,
      p_nuevo_profesional_uuid: nuevoProfesionalUuid
    });

    if (error) {
      console.error('[CalendarService] Error RPC al reagendar cita:', error.message);
      return { success: false, message: error.message };
    }

    if (data && !data.success) {
      console.warn('[CalendarService] Rechazo lógico en reagendamiento:', data.message);
      return { success: false, message: data.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('[CalendarService] Excepción al reagendar cita:', err);
    return { success: false, message: err?.message || 'Error desconocido' };
  }
}

/**
 * Crea una cita manual desde Secretaría. 
 * Si el paciente no existe, crea un "Shadow Profile" al vuelo.
 */
export interface CitaManualPayload {
  fechaInicioIso: string;
  fechaFinIso: string;
  profesionalUuid: string;
  pacienteUuid?: string; // Si es null o undefined, es un paciente sombra
  nuevoPacienteNombre?: string;
  nuevoPacienteTelefono?: string;
  nuevoPacienteCedula?: string;
  nuevoPacienteFechaNacimiento?: string;
  motivo?: string;
  montoCobrado?: number;
  moneda?: 'USD' | 'EUR';
  notaAdicional?: string;
  activeWorkspaceId?: string | null;
  activeWorkspaceType?: 'personal' | 'equipo';
  tipo?: 'cita_manual' | 'ocupado';
  alcance?: 'local' | 'global';
}

async function crearCitaParaPacienteSombra(payload: CitaManualPayload): Promise<{ success: boolean; message?: string }> {
  const {
    fechaInicioIso,
    fechaFinIso,
    profesionalUuid,
    nuevoPacienteNombre,
    nuevoPacienteTelefono,
    nuevoPacienteCedula,
    nuevoPacienteFechaNacimiento,
    motivo,
    montoCobrado,
    moneda,
    notaAdicional,
    tipo,
    alcance
  } = payload;

  const fechaObj = new Date(fechaInicioIso);
  const finObj = new Date(fechaFinIso);

  const yyyy = fechaObj.getFullYear();
  const mm = String(fechaObj.getMonth() + 1).padStart(2, '0');
  const dd = String(fechaObj.getDate()).padStart(2, '0');
  const fechaLocal = `${yyyy}-${mm}-${dd}`;

  const startH = String(fechaObj.getHours()).padStart(2, '0');
  const startM = String(fechaObj.getMinutes()).padStart(2, '0');
  const inicioTime = `${startH}:${startM}:00`;

  const endH = String(finObj.getHours()).padStart(2, '0');
  const endM = String(finObj.getMinutes()).padStart(2, '0');
  const finTime = `${endH}:${endM}:00`;

  let pPacienteData = null;
  let pMontoCobrado = null;
  let pMoneda = null;

  if (tipo !== 'ocupado') {
    pPacienteData = {
      nombre: nuevoPacienteNombre,
      telefono: nuevoPacienteTelefono,
      cedula: nuevoPacienteCedula,
      fecha_nacimiento: nuevoPacienteFechaNacimiento,
      motivo: motivo || undefined
    };
    if (montoCobrado) {
      pMontoCobrado = montoCobrado;
      pMoneda = moneda || 'USD';
    }
  }

  const citaEvento = {
    p_profesional_uuid: profesionalUuid,
    p_fecha: fechaLocal,
    p_inicio: inicioTime,
    p_fin: finTime,
    p_tipo: tipo || 'cita_manual',
    p_paciente_data: pPacienteData,
    p_monto_cobrado: pMontoCobrado,
    p_moneda: pMoneda,
    p_nota: notaAdicional || null,
    p_alcance: alcance || 'local'
  };

  const { data, error: eError } = await supabase.rpc('fn_crear_cita_manual', citaEvento);

  if (eError) {
    console.error('[CalendarService] Error al llamar a fn_crear_cita_manual:', eError);
    return { success: false, message: eError.message };
  }

  if (data && !data.success) {
    console.warn('[CalendarService] Choque detectado en fn_crear_cita_manual:', data.message);
    return { success: false, message: data.message };
  }

  return { success: true };
}

async function crearCitaParaPacienteRegistradoEquipo(payload: CitaManualPayload): Promise<{ success: boolean; message?: string }> {
  const { fechaInicioIso, fechaFinIso, profesionalUuid, pacienteUuid, activeWorkspaceId } = payload;
  
  // Insert into agendamiento_equipo
  const nuevaCitaEquipo = {
    equipo_uuid: activeWorkspaceId,
    paciente_uuid: pacienteUuid,
    fecha_hora_inicio: fechaInicioIso,
    fecha_hora_fin: fechaFinIso,
    estado: 'confirmada',
  };
  const { data: equipoData, error: eError } = await supabase
    .from('agendamiento_equipo')
    .insert(nuevaCitaEquipo)
    .select('cita_equipo_id')
    .single();
  
  if (eError) {
    console.error('[CalendarService] Error al crear la cita en agendamiento_equipo:', eError);
    return { success: false, message: eError.message };
  }

  // Añadir la asistencia (quién atiende de ese equipo)
  const { error: asisError } = await supabase
    .from('cita_equipo_asistencias')
    .insert({
      cita_equipo_id: equipoData.cita_equipo_id,
      miembro_uuid: profesionalUuid
    });
  
  if (asisError) {
    console.error('[CalendarService] Error al crear cita_equipo_asistencias:', asisError);
  }
  return { success: true };
}

async function crearCitaParaPacienteRegistrado(payload: CitaManualPayload): Promise<{ success: boolean; message?: string }> {
  const {
    fechaInicioIso,
    fechaFinIso,
    profesionalUuid,
    pacienteUuid,
    motivo,
    montoCobrado,
    moneda,
    notaAdicional,
    activeWorkspaceId = null,
    activeWorkspaceType = 'personal',
    tipo,
    alcance
  } = payload;

  if (activeWorkspaceType === 'equipo' && activeWorkspaceId) {
    return await crearCitaParaPacienteRegistradoEquipo(payload);
  }
    // Se inserta en 'agenda_eventos' como cita_manual para evitar el TTL de reservas digitales
    const fechaObj = new Date(fechaInicioIso);
    const finObj = new Date(fechaFinIso);

    const yyyy = fechaObj.getFullYear();
    const mm = String(fechaObj.getMonth() + 1).padStart(2, '0');
    const dd = String(fechaObj.getDate()).padStart(2, '0');
    const fechaLocal = `${yyyy}-${mm}-${dd}`;

    const startH = String(fechaObj.getHours()).padStart(2, '0');
    const startM = String(fechaObj.getMinutes()).padStart(2, '0');
    const inicioTime = `${startH}:${startM}:00`;

    const endH = String(finObj.getHours()).padStart(2, '0');
    const endM = String(finObj.getMinutes()).padStart(2, '0');
    const finTime = `${endH}:${endM}:00`;

    let pPacienteUuid = null;
    let pPacienteData = null;
    let pMontoCobrado = null;
    let pMoneda = null;

    if (tipo !== 'ocupado') {
      pPacienteUuid = pacienteUuid;
      pPacienteData = motivo ? { motivo } : null;
      if (montoCobrado) {
        pMontoCobrado = montoCobrado;
        pMoneda = moneda || 'USD';
      }
    }

    const citaEvento = {
      p_profesional_uuid: profesionalUuid,
      p_fecha: fechaLocal,
      p_inicio: inicioTime,
      p_fin: finTime,
      p_tipo: tipo || 'cita_manual',
      p_paciente_uuid: pPacienteUuid,
      p_paciente_data: pPacienteData,
      p_monto_cobrado: pMontoCobrado,
      p_moneda: pMoneda,
      p_nota: notaAdicional || null,
      p_alcance: alcance || 'local'
    };

    const { data, error: cError } = await supabase.rpc('fn_crear_cita_manual', citaEvento);

    if (cError) {
      console.error('[CalendarService] Error al llamar a fn_crear_cita_manual:', cError);
      return { success: false, message: cError.message };
    }

    if (data && !data.success) {
      console.warn('[CalendarService] Choque detectado en fn_crear_cita_manual:', data.message);
      return { success: false, message: data.message };
    }

    return { success: true };
  }

export async function crearCitaManual(payload: CitaManualPayload): Promise<{ success: boolean; message?: string }> {
  try {
    if (!payload.pacienteUuid) {
      return await crearCitaParaPacienteSombra(payload);
    } else {
      return await crearCitaParaPacienteRegistrado(payload);
    }
  } catch (err: any) {
    console.error('[CalendarService] Error al crear cita manual:', err);
    return { success: false, message: err?.message || 'Error desconocido' };
  }
}

// ─── Servicios para Checkout ──────────────────────────────────────────────────

export async function fetchServiciosProfesional(profesionalUuid: string) {
  const { data, error } = await supabase
    .from('profesional_servicios_faq')
    .select('id, titulo_opcion, precio_sugerido, moneda')
    .eq('profesional_uuid', profesionalUuid);

  if (error) {
    console.error('[CalendarService] Error fetching servicios:', error);
    return [];
  }
  return data || [];
}

export interface CheckoutPayload {
  cita_id: string;
  fecha_hora_inicio?: string;
  fecha_hora_fin?: string;
  profesional_uuid?: string;
  servicios: Array<{
    servicio_id: number;
    cantidad: number;
    precio_unitario: number;
    moneda: string;
  }>;
  pago?: {
    monto_pagado: number;
    banco_emisor: string;
    referencia_6_digitos: string;
    telefono_emisor: string;
  };
}

export async function checkoutCita(payload: CheckoutPayload): Promise<{ success: boolean; message?: string }> {
  try {
    const { data, error } = await supabase.rpc('fn_checkout_cita', {
      p_cita_id: payload.cita_id,
      p_servicios: payload.servicios,
      p_pago: payload.pago || null,
      p_fecha_hora_inicio: payload.fecha_hora_inicio || null,
      p_fecha_hora_fin: payload.fecha_hora_fin || null,
      p_profesional_uuid: payload.profesional_uuid || null
    });

    if (error) {
      console.error('[CalendarService] Error rpc fn_checkout_cita:', error);
      return { success: false, message: error.message };
    }

    if (!data?.success) {
      return { success: false, message: data?.error || 'Error desconocido al procesar checkout' };
    }

    return { success: true };
  } catch (err: any) {
    console.error('[CalendarService] Exception checkoutCita:', err);
    return { success: false, message: err?.message };
  }
}

// ─── Buscador Inteligente (Mapa de Calor) ─────────────────────────────────────

export interface BloqueOcupado {
  inicioIso: string;
  finIso: string;
}

/**
 * Bulk Fetch mensual (O(1) request) con Payload Trimming.
 * Solo extrae los límites de tiempo (sin datos clínicos ni de paciente)
 * para calcular disponibilidad en el frontend.
 */
export async function fetchDisponibilidadMensual(
  profesionalUuid: string,
  fechaInicioIso: string,
  fechaFinIso: string
): Promise<BloqueOcupado[]> {
  const bloques: BloqueOcupado[] = [];
  
  // 1. Citas Digitales
  const { data: citasData, error: citasError } = await supabase
    .from('agendamiento_citas')
    .select('fecha_hora_inicio, fecha_hora_fin')
    .eq('profesional_uuid', profesionalUuid)
    .gte('fecha_hora_inicio', fechaInicioIso)
    .lte('fecha_hora_inicio', fechaFinIso)
    .not('estado', 'in', '("expirada","rechazada","cancelada_paciente")');

  if (citasError) {
    console.error('[CalendarService] Error fetching citas bulk:', citasError.message);
  } else if (citasData) {
    for (const c of citasData) {
      bloques.push({
        inicioIso: c.fecha_hora_inicio,
        finIso: c.fecha_hora_fin
      });
    }
  }

  // 2. Eventos Manuales (Bloqueos)
  const startLocal = fechaInicioIso.split('T')[0];
  const endLocal = fechaFinIso.split('T')[0];

  const { data: eventosData, error: eventosError } = await supabase
    .from('agenda_eventos')
    .select('fecha, inicio, fin')
    .eq('profesional_uuid', profesionalUuid)
    .gte('fecha', startLocal)
    .lte('fecha', endLocal);

  if (eventosError) {
    console.error('[CalendarService] Error fetching eventos bulk:', eventosError.message);
  } else if (eventosData) {
    for (const e of eventosData) {
      const [year, month, day] = e.fecha.split('-').map(Number);
      const [sh, sm, ss] = e.inicio.split(':').map(Number);
      const [eh, em, es] = e.fin.split(':').map(Number);
      
      const startDT = new Date(year, month - 1, day, sh, sm, ss || 0);
      const endDT = new Date(year, month - 1, day, eh, em, es || 0);
      
      bloques.push({
        inicioIso: startDT.toISOString(),
        finIso: endDT.toISOString()
      });
    }
  }

  return bloques;
}
